<template>
	<div>
		<div
			class="overflow-hidden shadow ring-opacity-5 ring-1 ring-black rounded-lg sm:rounded-xl bg-white overflow-hidden"
		>
			<table
				class="min-w-full max-w-full sm:divide-y sm:divide-gray-300 h-[1px]"
			>
				<thead class="bg-gray-50 hidden sm:table-header-group">
					<tr>
						<th
							scope="col"
							class="hidden sm:table-cell py-3.5 pl-4 text-left font-semibold text-gray-500"
						>
							{{ __("Date") }}
						</th>
						<th v-if="showUserLocations"
								scope="col"
								class="hidden lg:table-cell pl-4 py-3.5 text-left font-semibold text-gray-500"
						>
							{{ __('userLocation') }}
						</th>
						<th
							scope="col"
							class="hidden lg:table-cell py-3.5 pl-4 pr-4 text-left font-semibold text-gray-500"
						>
							#
						</th>
						<th
							scope="col"
							class="hidden sm:table-cell pl-4 py-3.5 text-left font-semibold text-gray-500"
						>
							{{ __(filterType == "SELL" ? "Buyer" : "Seller") }}
						</th>
						<th
							scope="col"
							class="hidden sm:table-cell pl-4 py-3.5 text-left font-semibold text-gray-500"
						>
							{{ __("Quantity") }}
						</th>
						<th
							scope="col"
							class="hidden sm:table-cell pl-4 py-3.5 text-left font-semibold text-gray-500"
						>
							{{ __("Article") }}
						</th>
						<th
							scope="col"
							class="hidden sm:table-cell pl-4 py-3.5 text-left font-semibold text-gray-500"
						>
							{{ __("Harvest") }}
						</th>
						<th
							scope="col"
							class="hidden sm:table-cell pl-4 py-3.5 text-left font-semibold text-gray-500"
						>
							{{ __("Fulfilment Date") }}
						</th>
						<th
							scope="col"
							class="hidden sm:table-cell pl-4 py-3.5 text-left font-semibold text-gray-500"
						>
							<span class="sm:hidden">{{ __("Date") }}<br /></span>
							{{ __("Price") }}
						</th>
						<th
							scope="col"
							class="hidden sm:table-cell relative py-3.5 pr-4 font-semibold text-gray-500 text-right"
						>
							Kontrakt
							<span class="sr-only">Download</span>
						</th>
					</tr>
				</thead>
				<tbody class="divide-y divide-gray-200 bg-white">
					<tr v-for="contract in contracts" class="sm:leading-5">
						<td
							class="hidden sm:table-cell whitespace-nowrap pl-4 py-4 text-gray-500"
						>
							<span v-if="!contract.is_external_contract">{{
								formatDate(new Date(contract.created_at))
							}}</span>
							<span v-else>{{
								formatDate(new Date(contract.contract_date))
							}}</span>
							<!-- to do date format, e.g. 01.04.2022 -->
						</td>
						<td class="hidden lg:table-cell pl-4 py-4" v-if="showUserLocations">
							<ui-tooltip class="font-normal"
													:text="userLocationIsEditable(contract.user_location_id) ? 'Lagerstandort zuweisen' : 'Erstellen Sie einen Lagerstandort.'"
							>
								<template v-slot:label>
									<ui-select-group
										:disabled="!userLocationIsEditable(contract.user_location_id)"
										class="max-w-40"
										:form="form"
										v-model="contract.user_location_id"
										field="user_location_id"
										type="select"
										:options="userLocations"
										:required="true"
										:placeholder="false"
										@input="updateUserLocation(contract, $event)"
									></ui-select-group>
								</template>
							</ui-tooltip>
						</td>
						<td class="pl-4 py-4 hidden lg:table-cell"><span v-if="!contract.is_external_contract">{{ contract.id }}</span></td>
						<td class="pl-4 py-4 text-gray-500">
							<div class="text-gray-700 font-medium max-w-44 sm:max-w-28 truncate">
								<span v-if="!contract.is_external_contract"
											class="truncate"
								>
									{{ contract.counterpart.company_name }}
								</span>
								<span v-else
											class="truncate">
									<span v-if="contract.buyer_name">
										{{ contract.buyer_name }}
									</span>
									<span v-else class="italic text-gray-500">
										{{ __("N/A") }}
									</span>
								</span>
							</div>
							<div class="sm:hidden flex gap-x-4">
								<span
									>{{ contract.qty__mt }} t
									{{
										$cropOptions.getLocalizedLabel(
											contract.product__grade,
											$cropOptions.productGradeOptions
										)
									}}</span
								>
								<span>{{ __("Harvest") }} {{ contract.product__harvest }}</span>
							</div>
							<div class="sm:hidden grid grid-cols-4 gap-x-4 items-end">
								<span class="col-span-3">
									{{
										$cropOptions.getLocalizedLabel(
											contract.location__mode,
											$cropOptions.locationModeOptions
										)
									}}
									{{ contract.location__address__zip }}
									{{ contract.location__address__city }}
								</span>
								<span class="text-gray-500 whitespace-nowrap">
									{{ formatDate(new Date(contract.created_at)) }}
								</span>
							</div>

							<div class="sm:hidden">
								<ui-select-group
									:disabled="!userLocationIsEditable(contract.user_location_id)"
									:form="form"
									v-model="contract.user_location_id"
									field="user_location_id"
									type="select"
									:options="userLocations"
									:required="true"
									:placeholder="false"
									@input="updateUserLocation(contract, $event)"
								></ui-select-group>
							</div>
						</td>
						<td
							class="hidden sm:table-cell whitespace-nowrap pl-4 py-4 text-gray-500"
						>
							{{ contract.qty__mt }} t
						</td>
						<td
							class="hidden sm:table-cell whitespace-nowrap pl-4 py-4 text-gray-500"
						>
							{{
								$cropOptions.getLocalizedLabel(
									contract.product__grade,
									$cropOptions.productGradeOptions
								)
							}}
						</td>
						<td
							class="hidden sm:table-cell whitespace-nowrap pl-4 py-4 text-gray-500"
						>
							{{ contract.product__harvest }}
						</td>
						<td class="hidden sm:table-cell pl-4 py-4 text-gray-500">
							<div class="w-full" v-if="isDummyData">
								{{ __("At harvest") }}
							</div>
							<div class="w-full" v-else>
								{{ contract.contract_date_string }}
							</div>
						</td>
						<td
							class="sm:hidden align-top h-full whitespace-nowrap text-right sm:text-left pr-4 sm:pr-0 sm:pl-4 py-4"
						>
							<div
								class="h-full flex flex-col justify-between text-gray-700 font-medium"
							>
								{{
									(1 * contract.price__mt).toLocaleString(locale, {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})
								}}
								€/t

								<a
									:href="$route('contract.pdf', { id: contract.id })"
									type="button"
									class="sm:hidden mt-3 ml-auto block w-max py-3 px-3 rounded-lg bg-orange-500 text-white focus:ring-2 focus:ring-orange-200 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-orange-400 shadow hover:shadow-md hover:bg-orange-600 focus:outline-none"
								>
									<ui-icon name="download" stroke-width="2" class="w-5 h-5" />
								</a>
							</div>
						</td>
						<td
							class="hidden sm:table-cell whitespace-nowrap px-4 py-3 text-gray-700 font-medium"
						>
							{{
								(1 * contract.price__mt).toLocaleString(locale, {
									minimumFractionDigits: 2,
									maximumFractionDigits: 2,
								})
							}}
							€/t
						</td>
						<td
							class="hidden sm:table-cell whitespace-nowrap px-4 py-3 text-gray-500 text-right"
						>
							<div class="flex items-center justify-end">
								<button
									v-if="contract.is_external_contract"
									class="rounded-lg p-2 mr-1 bg-orange-500 text-white focus:ring-2 focus:ring-orange-200 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-orange-400 shadow hover:shadow-md hover:bg-orange-600 focus:outline-none"
									:class="['text-orange-500']"
									v-on:click="
									$bus.$emit(
										'toggle-iframe-modal',
										$route('external_contract.edit', {
											id: contract.id,
										})
									)
								"
								>
									<ui-icon class="h-5 w-5" name="edit" stroke-width="2.5" />
								</button>

								<ui-icon
									v-else
									name="logo"
									class="h-8 w-auto mx-auto inline-block mr-2"
								/>

								<!-- todo debug, prüfen ob interner vertrag auch downloadbar ist. -->
								<a
									v-if="contract.is_external_contract"
									:href="$route('external_contract.pdf', { id: contract.id })"
									type="button"
									target="_blank"
									class="inline-block p-2 rounded-lg bg-orange-500 text-white focus:ring-2 focus:ring-orange-200 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-orange-400 shadow hover:shadow-md hover:bg-orange-600 focus:outline-none"
								>
									<ui-icon name="download" stroke-width="2" class="w-5 h-5" />
								</a>

								<a
									v-else
									:href="$route('contract.pdf', { id: contract.id })"
									type="button"
									target="_blank"
									class="inline-block w-max py-2 px-2 rounded-lg bg-orange-500 text-white focus:ring-2 focus:ring-orange-200 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-orange-400 shadow hover:shadow-md hover:bg-orange-600 focus:outline-none"
								>
									<ui-icon name="download" stroke-width="2" class="w-5 h-5" />
								</a>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>

		<div class="sm:hidden mt-6 text-gray-500 flex gap-x-1.5">
			<ui-icon name="download" stroke-width="2" class="w-5 h-5 inline-block" />
			<span>=</span> <span>Kontrakt dowloaden</span>
		</div>
	</div>
</template>

<script>
function padTo2Digits(num) {
	return num.toString().padStart(2, "0");
}

export default {
	props: {
		user: {
			type: Object,
		},
		contracts: {
			type: Array,
			required: true,
		},
		isDummyData: {
			type: Boolean,
			default: false,
			required: false,
		},
		filterType: {
			type: String,
			required: true,
		},
		userLocations: {
			type: Array,
			default: () => [],
		}
	},
	data() {
		return {
			form: {}
		}
	},
	computed: {
		showUserLocations() {
			return ['FARMER', 'OTHER'].includes(this.user.type);
		},
	},
	methods: {
		formatDate(date) {
			return [
				padTo2Digits(date.getDate()),
				padTo2Digits(date.getMonth() + 1),
				String(date.getFullYear()).slice(-2),
			].join(".");
		},
		userLocationIsEditable(id) {
			if(this.userLocations.length > 1){
				return true;
			}

			const selectedUserLocation = this.userLocations.find((location) => location.id === id);
			return !selectedUserLocation;
		},
		updateUserLocation(contract, userLocationId) {

			this.form.user_location_id = userLocationId;
			const params = {id: contract.id};
			let route = this.$route('contract.user_location', params);
			if(contract.is_external_contract){
				route = this.$route('external_contract.user_location', params);
			}

			axios
				.patch(route,this.form)
				.then((result) => this.onSuccess(result))
				.catch((error) => this.onError(error));
		},
		onSuccess() {},
		onError() {},
	},
};
</script>
